import { Box, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getEngagementInsights } from 'API/engagement';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HBarList, Histogram } from '../../shared/charts';
import { fmtInt, fmtPct } from '../../shared/format';
import { MetricText } from '../../shared/metric';
import { KpiRow, KpiTile, QueryState, SectionCard } from '../../shared/ui';
import { normalizeFunnel } from '../engagement-logic';

const InsightsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useQuery({ queryKey: ['engagement', 'insights'], queryFn: () => getEngagementInsights() });
  const d = q.data;
  const funnel = normalizeFunnel(d?.funnel);
  const top = funnel[0]?.value ?? 0;
  const lowTouchCount = d?.lowTouch?.value ?? 0;
  const lowTouchOf = d?.lowTouch?.of;
  const kills = d?.killSwitchIncidents?.count ?? 0;
  const buckets = (d?.sendsPerUserWeek ?? []).slice(0, 5).map((value, i) => ({ label: i === 4 ? '4+' : String(i), value }));

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d ? (
        <Stack gap={2.5}>
          <KpiRow>
            <KpiTile label={t('engKpiUnsubscribeRate')} value={<MetricText value={d.unsubscribeRate} format={fmtPct(lng, 1)} variant={'kpi'} />} />
            <KpiTile
              label={t('engKpiLowTouch')}
              value={fmtInt(lng)(lowTouchCount)}
              sub={lowTouchOf !== undefined ? t('engOfTotal', { total: lowTouchOf }) : t('engKpiLowTouchSub')}
            />
            <KpiTile label={t('engKpiKillSwitch')} value={fmtInt(lng)(kills)} tone={kills > 0 ? 'warn' : 'default'} />
          </KpiRow>
          <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <SectionCard title={t('engInsightsFunnel')}>
                {funnel.length ? (
                  <HBarList
                    rows={funnel.map((f) => ({
                      label: t(`engFunnelStep_${f.step}`, { defaultValue: f.step }),
                      value: f.value,
                      hint: top > 0 && f.step !== funnel[0].step ? fmtPct(lng)(f.value / top) : undefined,
                    }))}
                  />
                ) : (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engEmpty')}</Typography>
                )}
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>{t('engInsightsCompletedNote')}</Typography>
              </SectionCard>
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <SectionCard title={t('engInsightsHistogram')}>
                {buckets.length ? <Histogram buckets={buckets} ariaLabel={t('engInsightsHistogram')} /> : <Typography sx={{ fontSize: 13 }}>{t('engEmpty')}</Typography>}
              </SectionCard>
            </Box>
          </Stack>
          <SectionCard title={t('engInsightsSkips')}>
            {Object.keys(d.skips ?? {}).length ? (
              <HBarList
                color={'#A0A0B8'}
                rows={Object.entries(d.skips)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, value]) => ({ label: t(`engSkip_${reason}`, { defaultValue: reason }), value }))}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engEmpty')}</Typography>
            )}
          </SectionCard>
        </Stack>
      ) : null}
    </QueryState>
  );
};

export default InsightsTab;
